<script setup>
import { computed, useTemplateRef } from 'vue';
import { useWindowSize, useElementBounding } from '@vueuse/core';

defineProps({
  option: {
    type: Object,
    default: () => {},
  },
  subMenuAvailable: {
    type: Boolean,
    default: true,
  },
});

const menuRef = useTemplateRef('menuRef');
const { width: windowWidth, height: windowHeight } = useWindowSize();
const { bottom, right } = useElementBounding(menuRef);

// Vertical position
const verticalPosition = computed(() => {
  const SUBMENU_HEIGHT = 240; // 15rem in pixels
  const spaceBelow = windowHeight.value - bottom.value;
  return spaceBelow < SUBMENU_HEIGHT ? 'bottom-0' : 'top-0';
});

// Horizontal position
const horizontalPosition = computed(() => {
  const SUBMENU_WIDTH = 240;
  const spaceRight = windowWidth.value - right.value;
  return spaceRight < SUBMENU_WIDTH ? 'right-full' : 'left-full';
});

const submenuPosition = computed(() => [
  verticalPosition.value,
  horizontalPosition.value,
]);
</script>

<template>
  <div
    ref="menuRef"
    class="advup-menu__item msm"
    :class="{ 'msm--inerte': !subMenuAvailable }"
    role="menuitem"
    aria-haspopup="menu"
    :aria-disabled="!subMenuAvailable"
    tabindex="0"
  >
    <!-- O item que abre um submenu (Prioridade, Etiquetas, Agente, Time).

         🔴 O SUBMENU ERA UM VÉU LEITOSO (`bg-n-alpha-3` + desfoque de 100px) —
         o mesmo defeito que o menu principal já tinha corrigido: sobre a lista,
         o texto de baixo vazava através das opções. Agora é a peça do produto
         (`.advup-menu`), superfície sólida. -->
    <fluent-icon :icon="option.icon" size="16" class="msm__ico" />
    <span class="msm__txt">{{ option.label }}</span>
    <fluent-icon icon="chevron-right" size="12" class="msm__seta" />
    <div
      v-if="subMenuAvailable"
      class="advup-menu msm__sub"
      :class="submenuPosition"
      role="menu"
    >
      <slot />
    </div>
  </div>
</template>

<style scoped>
.msm {
  position: relative;
  min-width: 200px;
}

.msm__ico {
  flex-shrink: 0;
  color: rgb(var(--slate-11));
}

.msm__txt {
  flex: 1;
  min-width: 0;
}

.msm__seta {
  flex-shrink: 0;
  color: rgb(var(--slate-10));
  transition: translate var(--mov-2) var(--mov-curva);
}

/* A seta anda um passo na direção em que o submenu vai abrir. */
.msm:hover .msm__seta {
  translate: 2px 0;
}

.msm__sub {
  position: absolute;
  display: none;
  overflow-x: hidden;
  overflow-y: auto;
  max-height: 15rem;
  cursor: pointer;
}

.msm:hover > .msm__sub,
.msm:focus-within > .msm__sub {
  display: block;
  animation: msm-abre var(--mov-2) var(--mov-curva) both;
}

@keyframes msm-abre {
  from {
    opacity: 0;
    translate: -4px 0;
  }
}

.msm--inerte,
.msm--inerte:hover {
  background: transparent;
  color: rgb(var(--slate-10));
  cursor: not-allowed;
}

@media (prefers-reduced-motion: reduce) {
  .msm__seta {
    transition: none;
  }

  .msm:hover .msm__seta {
    translate: none;
  }

  .msm:hover > .msm__sub {
    animation: none;
  }
}
</style>
